// Create / edit invoice modal — POST /billing/invoices in create mode,
// PATCH /billing/invoices/:id in edit mode (TASK-N31; backend gap closed
// by TASK-N30 at 6af2554, draft-only status gate enforced server-side).

import { useState, useEffect } from "react";
import type { JSX, FormEvent } from "react";
import { apiFetch } from "../lib/api.js";
import { Modal } from "./Modal.js";
import { FormField } from "./FormField.js";
import type { Invoice } from "../lib/billing.js";

type Mode = "create" | "edit";

interface Props {
  onClose: () => void;
  onCreated: () => void;
  mode?: Mode;
  initialValues?: Invoice;
}

interface SR { _id: string; service_type: string; scheduled_datetime?: string; }

interface CreateInvoicePayload {
  patient_id:      string;
  amount_paise:    number;
  sr_id?:          string;
  payment_method?: string;
  due_date?:       string;
  notes?:          string;
}

interface UpdateInvoicePayload {
  amount_paise?:   number;
  payment_method?: string;
  due_date?:       string;
  notes?:          string;
}

interface FieldErrors { patient_id?: string; amount?: string; }

const PAYMENT_METHODS = [
  { value: "",              label: "Select method…" },
  { value: "bank_transfer", label: "Bank transfer" },
  { value: "upi",           label: "UPI" },
  { value: "cash",          label: "Cash" },
  { value: "stripe",        label: "Stripe" },
  { value: "other",         label: "Other" },
];

const SEL_CLS = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-navy-500";

function srLabel(sr: SR): string {
  const date = sr.scheduled_datetime ? sr.scheduled_datetime.slice(0, 10) : "—";
  return `${date} — ${sr.service_type}`;
}

function validate(patientId: string, amount: string): FieldErrors {
  const errs: FieldErrors = {};
  if (!patientId.trim()) errs.patient_id = "Patient ID is required.";
  if (!amount.trim() || isNaN(Number(amount)) || Number(amount) <= 0)
    errs.amount = "Enter a valid amount greater than 0.";
  return errs;
}

interface FormVals {
  patientId: string; srId: string; amount: string;
  dueDate: string; notes: string; paymentMethod: string;
}

function buildCreatePayload(v: FormVals): CreateInvoicePayload {
  const payload: CreateInvoicePayload = {
    patient_id:   v.patientId.trim(),
    amount_paise: Math.round(Number(v.amount) * 100),
  };
  if (v.srId)          payload.sr_id          = v.srId;
  if (v.paymentMethod) payload.payment_method = v.paymentMethod;
  if (v.dueDate)       payload.due_date       = v.dueDate;
  if (v.notes.trim())  payload.notes          = v.notes.trim();
  return payload;
}

// Edit mode sends all 4 PATCHable fields per N30 contract; backend
// refine guards against empty-body, but with 4 fields always present
// the request is unambiguous and idempotent.
function buildEditPayload(v: FormVals): UpdateInvoicePayload {
  const payload: UpdateInvoicePayload = {
    amount_paise: Math.round(Number(v.amount) * 100),
    notes:        v.notes,
  };
  if (v.paymentMethod) payload.payment_method = v.paymentMethod;
  if (v.dueDate)       payload.due_date       = v.dueDate;
  return payload;
}

async function submitInvoice(
  args: {
    vals: FormVals; mode: Mode; invoiceId?: string;
    setErrors: (e: FieldErrors) => void; setApiError: (e: string | null) => void;
    setSaving: (s: boolean) => void; onCreated: () => void; onClose: () => void;
  },
): Promise<void> {
  const { vals, mode, invoiceId, setErrors, setApiError, setSaving, onCreated, onClose } = args;
  setApiError(null);
  const errs = validate(vals.patientId, vals.amount);
  if (Object.keys(errs).length > 0) { setErrors(errs); return; }
  setErrors({}); setSaving(true);
  try {
    if (mode === "edit" && invoiceId) {
      await apiFetch(`/billing/invoices/${invoiceId}`, {
        method: "PATCH",
        body:   JSON.stringify(buildEditPayload(vals)),
      });
    } else {
      await apiFetch("/billing/invoices", {
        method: "POST",
        body:   JSON.stringify(buildCreatePayload(vals)),
      });
    }
    onCreated(); onClose();
  } catch (err) {
    setApiError(err instanceof Error ? err.message : "Failed to save invoice.");
  } finally { setSaving(false); }
}

interface FormState {
  patientId: string;     setPatientId: (v: string) => void;
  srId: string;          setSrId:      (v: string) => void;
  srs: SR[];             setSrs:       (s: SR[]) => void;
  loadingSrs: boolean;   setLoadingSrs:(b: boolean) => void;
  amount: string;        setAmount:    (v: string) => void;
  dueDate: string;       setDueDate:   (v: string) => void;
  notes: string;         setNotes:     (v: string) => void;
  paymentMethod: string; setPaymentMethod: (v: string) => void;
  errors: FieldErrors;   setErrors:    (e: FieldErrors) => void;
  apiError: string | null; setApiError: (e: string | null) => void;
  saving: boolean;       setSaving:    (b: boolean) => void;
}

interface InvoiceDefaults {
  patientId: string; srId: string; amount: string;
  dueDate: string; notes: string; paymentMethod: string;
}

// eslint-disable-next-line complexity -- many independent conditional branches; per-branch extraction would split into co-equal helpers without semantic gain. Tracked under TASK-N19 Phase B.
function defaultsFromInvoice(i?: Invoice): InvoiceDefaults {
  return {
    patientId:     i?.patient_id ?? "",
    srId:          i?.sr_id ?? "",
    amount:        i ? (i.amount_paise / 100).toString() : "",
    dueDate:       i?.due_date ? i.due_date.slice(0, 10) : "",
    notes:         i?.notes ?? "",
    paymentMethod: i?.payment_method ?? "",
  };
}

function useFormState(initialValues?: Invoice): FormState {
  const d = defaultsFromInvoice(initialValues);
  const [patientId,     setPatientId]     = useState(d.patientId);
  const [srId,          setSrId]          = useState(d.srId);
  const [srs,           setSrs]           = useState<SR[]>([]);
  const [loadingSrs,    setLoadingSrs]    = useState(false);
  const [amount,        setAmount]        = useState(d.amount);
  const [dueDate,       setDueDate]       = useState(d.dueDate);
  const [notes,         setNotes]         = useState(d.notes);
  const [paymentMethod, setPaymentMethod] = useState(d.paymentMethod);
  const [errors,        setErrors]        = useState<FieldErrors>({});
  const [apiError,      setApiError]      = useState<string | null>(null);
  const [saving,        setSaving]        = useState(false);
  return {
    patientId, setPatientId, srId, setSrId, srs, setSrs,
    loadingSrs, setLoadingSrs, amount, setAmount, dueDate, setDueDate,
    notes, setNotes, paymentMethod, setPaymentMethod,
    errors, setErrors, apiError, setApiError, saving, setSaving,
  };
}

function srPlaceholderText(loadingSrs: boolean, patientId: string, srsLen: number): string {
  if (loadingSrs)              return "Loading…";
  if (patientId.length !== 36) return "Enter patient ID first";
  if (srsLen === 0)            return "No completed SRs found";
  return "Select service request…";
}

function SrSelect({
  isEdit, srId, setSrId, srs, loadingSrs, patientId,
}: {
  isEdit: boolean; srId: string; setSrId: (v: string) => void;
  srs: SR[]; loadingSrs: boolean; patientId: string;
}): JSX.Element {
  return (
    <div>
      <label htmlFor="sr-select" className="block text-sm font-medium text-slate-700 mb-1">
        Linked service request
      </label>
      <select id="sr-select" value={srId} onChange={(e) => setSrId(e.target.value)}
        disabled={isEdit || loadingSrs || srs.length === 0}
        className={`${SEL_CLS} disabled:bg-slate-50 disabled:text-slate-400`}>
        {isEdit
          ? <option value={srId}>{srId || "—"}</option>
          : <>
              <option value="">{srPlaceholderText(loadingSrs, patientId, srs.length)}</option>
              {srs.map((sr) => <option key={sr._id} value={sr._id}>{srLabel(sr)}</option>)}
            </>}
      </select>
    </div>
  );
}

function InvoiceFormBody({
  s, isEdit, submitLabel, onSubmit, onClose,
}: {
  s: FormState; isEdit: boolean; submitLabel: string;
  onSubmit: (e: FormEvent) => void; onClose: () => void;
}): JSX.Element {
  return (
    <form onSubmit={onSubmit} noValidate className="space-y-4">
      <FormField label="Patient ID" value={s.patientId} onChange={s.setPatientId} required
        placeholder="Patient UUID" error={s.errors.patient_id} disabled={isEdit} />
      <SrSelect isEdit={isEdit} srId={s.srId} setSrId={s.setSrId} srs={s.srs}
        loadingSrs={s.loadingSrs} patientId={s.patientId} />
      <FormField label="Amount (₹)" type="number" value={s.amount} onChange={s.setAmount}
        required placeholder="0.00" error={s.errors.amount} />
      <div>
        <label htmlFor="payment-method" className="block text-sm font-medium text-slate-700 mb-1">
          Payment method
        </label>
        <select id="payment-method" value={s.paymentMethod} onChange={(e) => s.setPaymentMethod(e.target.value)}
          className={SEL_CLS}>
          {PAYMENT_METHODS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <FormField label="Due Date" type="date" value={s.dueDate} onChange={s.setDueDate} />
      <div>
        <label htmlFor="inv-notes" className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
        <textarea id="inv-notes" value={s.notes} onChange={(e) => s.setNotes(e.target.value)}
          rows={3} placeholder="Optional notes…"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-500 resize-none" />
      </div>
      {s.apiError && <p role="alert" className="text-sm text-red-600">{s.apiError}</p>}
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose}
          className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">
          Cancel
        </button>
        <button type="submit" disabled={s.saving}
          className="px-4 py-2 text-sm rounded-lg bg-navy-700 text-white font-medium hover:bg-navy-800 disabled:opacity-50">
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

export function CreateInvoiceModal({ onClose, onCreated, mode = "create", initialValues }: Props): JSX.Element {
  const isEdit = mode === "edit";
  const s = useFormState(initialValues);

  // SR list fetch is a create-mode affordance; in edit mode the sr_id
  // is fixed (not in N30's PATCH schema) so skip the fetch entirely.
  useEffect(() => {
    if (isEdit) return;
    if (s.patientId.length !== 36) { s.setSrs([]); s.setSrId(""); return; }
    s.setLoadingSrs(true);
    apiFetch<{ data: SR[]; total: number; page: number; limit: number }>(
      `/sr?patient_id=${encodeURIComponent(s.patientId)}&status=completed`
    ).then((res) => s.setSrs(res.data ?? []))
      .catch(() => s.setSrs([]))
      .finally(() => s.setLoadingSrs(false));
  }, [s.patientId, isEdit]);

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    void submitInvoice({
      vals: {
        patientId: s.patientId, srId: s.srId, amount: s.amount,
        dueDate: s.dueDate, notes: s.notes, paymentMethod: s.paymentMethod,
      },
      mode, invoiceId: initialValues?._id,
      setErrors: s.setErrors, setApiError: s.setApiError, setSaving: s.setSaving,
      onCreated, onClose,
    });
  }

  const title       = isEdit ? "Edit Invoice"    : "Create Invoice";
  const submitLabel = isEdit
    ? (s.saving ? "Saving…"   : "Save Changes")
    : (s.saving ? "Creating…" : "Create Invoice");

  return (
    <Modal title={title} onClose={onClose}>
      <InvoiceFormBody s={s} isEdit={isEdit} submitLabel={submitLabel}
        onSubmit={onSubmit} onClose={onClose} />
    </Modal>
  );
}
